/**
 * Jaipur Rugs — Room Visualizer
 * ----------------------------------------------------------
 * Lets the customer composite the current product image onto a
 * pre-curated room photo. Supports drag, rotate (slider), pinch
 * scale, duplicate, remove, reset, compare mode, and download.
 *
 * Loaded as an ES module via importmap-free `<script type="module">`
 * to keep the dialog independent of the rest of the theme.
 * ----------------------------------------------------------
 */

import { Component } from '@theme/component';

class RoomVisualizerComponent extends Component {
  requiredRefs = ['dialog', 'selectorView', 'editorView', 'canvas', 'roomImage', 'rugLayer'];

  /** @type {Map<string, {x:number, y:number, rotation:number, scale:number, el:HTMLElement}>} */
  #rugs = new Map();
  /** @type {string|null} */ #selectedId = null;
  /** @type {number} */ #rugCounter = 0;
  /** @type {number} */ #savedScrollY = 0;
  /** @type {{id:string, startX:number, startY:number, originX:number, originY:number} | null} */
  #drag = null;
  /** @type {{distance:number, scale:number} | null} */ #pinch = null;
  /** @type {boolean} */ #listenersBound = false;
  /** @type {boolean} */ #attachedToGallery = false;
  /** @type {Set<string>} */ #wishlist = new Set();

  connectedCallback() {
    super.connectedCallback();
    // Bind once — `connectedCallback` fires again when we relocate via `attach=gallery`.
    if (!this.#listenersBound && this.refs.dialog) {
      this.refs.dialog.addEventListener('close', () => this.#onDialogClosed());
      this.refs.dialog.addEventListener('cancel', (e) => { e.preventDefault(); this.closeDialog(); });
      this.#listenersBound = true;
    }
    if (this.dataset.attach === 'gallery' && !this.#attachedToGallery) {
      this.#attachToGallery();
    }

    // Hydrate wishlist heart state from localStorage.
    this.#loadWishlist();
    this.#applyWishlistMarkers();
  }

  /* ──────────────────────────────────────────────
     Auto-attach to the product gallery
     ────────────────────────────────────────────── */

  #attachToGallery() {
    const tryAttach = () => {
      const gallery = document.querySelector('media-gallery');
      if (!gallery) return false;
      // Mark BEFORE the move — `insertAdjacentElement` fires connectedCallback
      // synchronously and we must not recurse.
      this.#attachedToGallery = true;
      gallery.insertAdjacentElement('afterend', this);
      // Tag the gallery so CSS can visually merge the button into the card.
      gallery.setAttribute('data-rv-host', 'true');
      return true;
    };

    if (tryAttach()) return;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => tryAttach(), { once: true });
    } else {
      // Gallery isn't in the DOM yet on this tick — try once on next frame.
      requestAnimationFrame(() => tryAttach());
    }
  }

  /* ──────────────────────────────────────────────
     Dialog open / close
     ────────────────────────────────────────────── */

  openDialog = () => {
    const { dialog } = this.refs;
    if (dialog.open) return;
    this.#savedScrollY = window.scrollY;
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.#savedScrollY}px`;
    dialog.showModal();
    this.#showSelector();
  };

  closeDialog = () => {
    const { dialog } = this.refs;
    if (!dialog.open) return;
    dialog.close();
  };

  #onDialogClosed() {
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#savedScrollY, behavior: 'instant' });
    // Clear any selection so the next open starts fresh.
    this.#deselectAll();
  }

  /* ──────────────────────────────────────────────
     View toggles
     ────────────────────────────────────────────── */

  #showSelector() {
    this.refs.selectorView.hidden = false;
    this.refs.editorView.hidden = true;
  }

  #showEditor() {
    this.refs.selectorView.hidden = true;
    this.refs.editorView.hidden = false;
  }

  backToSelector = () => {
    this.clearAllRugs();
    this.#showSelector();
  };

  showUploadHint = () => {
    const toast = this.refs.uploadToast;
    if (!toast) return;
    toast.hidden = false;
    clearTimeout(this._toastT);
    this._toastT = setTimeout(() => { toast.hidden = true; }, 2400);
  };

  /* ──────────────────────────────────────────────
     Room selection
     ────────────────────────────────────────────── */

  /** @param {MouseEvent} event */
  selectRoom = (event) => {
    const card = /** @type {HTMLElement} */ (event.target).closest('[data-room-src]');
    if (!(card instanceof HTMLElement)) return;

    const src = card.dataset.roomSrc;
    if (!src) return;

    const roomImg = /** @type {HTMLImageElement} */ (this.refs.roomImage);
    roomImg.crossOrigin = 'anonymous';
    roomImg.src = src;
    roomImg.alt = card.dataset.roomLabel || '';

    this.#showEditor();

    // Show the drag-this-product hint briefly.
    if (this.refs.dragHint) {
      this.refs.dragHint.hidden = false;
      clearTimeout(this._hintT);
      this._hintT = setTimeout(() => { this.refs.dragHint.hidden = true; }, 4000);
    }

    // Place the first rug centered.
    this.#rugs.clear();
    this.#rugCounter = 0;
    this.refs.rugLayer.replaceChildren();
    this.#addRug({ x: 0, y: 0, rotation: 0, scale: 1 });
  };

  /* ──────────────────────────────────────────────
     Rug instances
     ────────────────────────────────────────────── */

  #addRug(state) {
    const id = `rug-${++this.#rugCounter}`;
    const el = document.createElement('div');
    el.className = 'rv-rug';
    el.dataset.rugId = id;
    el.setAttribute('role', 'button');
    el.setAttribute('tabindex', '0');
    el.setAttribute('aria-label', 'Rug — drag to move, click to select');

    const img = document.createElement('img');
    img.crossOrigin = 'anonymous';
    img.draggable = false;
    img.alt = this.dataset.rugAlt || '';
    img.src = this.dataset.rugSrc || '';
    el.appendChild(img);

    // Center-pull marker (visible only when selected).
    const handle = document.createElement('span');
    handle.className = 'rv-rug__handle rv-rug__handle--move';
    handle.setAttribute('aria-hidden', 'true');
    handle.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14"><path d="M5 9l-3 3 3 3M19 9l3 3-3 3M9 5l3-3 3 3M9 19l3 3 3-3"/></svg>';
    el.appendChild(handle);

    this.refs.rugLayer.appendChild(el);
    this.#rugs.set(id, { ...state, el });
    this.#applyTransform(id);
    this.#bindRugInteractions(el, id);
    this.#selectRug(id);
    return id;
  }

  #applyTransform(id) {
    const rug = this.#rugs.get(id);
    if (!rug) return;
    const { x, y, rotation, scale } = rug;
    // Base position is centered (top:50%; left:50%); translate offset is in canvas pixels.
    rug.el.style.transform =
      `translate(calc(-50% + ${x}px), calc(-50% + ${y}px)) rotate(${rotation}deg) scale(${scale})`;
  }

  #selectRug(id) {
    if (this.#selectedId && this.#selectedId !== id) {
      const prev = this.#rugs.get(this.#selectedId);
      prev?.el.classList.remove('is-selected');
    }
    this.#selectedId = id;
    const rug = this.#rugs.get(id);
    if (!rug) return;
    rug.el.classList.add('is-selected');
    // Surface the transform panel and sync slider.
    if (this.refs.transformPanel) this.refs.transformPanel.hidden = false;
    if (this.refs.rotationSlider) {
      this.refs.rotationSlider.value = String(rug.rotation);
      this.#updateRotationLabel(rug.rotation);
    }
  }

  #deselectAll() {
    if (this.#selectedId) {
      this.#rugs.get(this.#selectedId)?.el.classList.remove('is-selected');
    }
    this.#selectedId = null;
    if (this.refs.transformPanel) this.refs.transformPanel.hidden = true;
  }

  deselectRug = () => this.#deselectAll();

  clearAllRugs = () => {
    this.#rugs.clear();
    this.#rugCounter = 0;
    this.refs.rugLayer.replaceChildren();
    this.#deselectAll();
  };

  duplicateRug = () => {
    if (!this.#selectedId) return;
    const src = this.#rugs.get(this.#selectedId);
    if (!src) return;
    this.#addRug({
      x: src.x + 24,
      y: src.y + 24,
      rotation: src.rotation,
      scale: src.scale,
    });
  };

  removeRug = () => {
    if (!this.#selectedId) return;
    const rug = this.#rugs.get(this.#selectedId);
    rug?.el.remove();
    this.#rugs.delete(this.#selectedId);
    this.#deselectAll();
  };

  resetRug = () => {
    if (!this.#selectedId) return;
    const rug = this.#rugs.get(this.#selectedId);
    if (!rug) return;
    rug.x = 0;
    rug.y = 0;
    rug.rotation = 0;
    rug.scale = 1;
    this.#applyTransform(this.#selectedId);
    if (this.refs.rotationSlider) this.refs.rotationSlider.value = '0';
    this.#updateRotationLabel(0);
  };

  /* ──────────────────────────────────────────────
     Pointer drag (single-pointer move + 2-pointer pinch scale)
     ────────────────────────────────────────────── */

  #bindRugInteractions(el, id) {
    /** @type {Map<number, PointerEvent>} */
    const active = new Map();

    el.addEventListener('pointerdown', (e) => {
      el.setPointerCapture(e.pointerId);
      active.set(e.pointerId, e);
      this.#selectRug(id);

      if (active.size === 1) {
        const rug = this.#rugs.get(id);
        if (!rug) return;
        this.#drag = {
          id,
          startX: e.clientX,
          startY: e.clientY,
          originX: rug.x,
          originY: rug.y,
        };
        el.classList.add('is-dragging');
      } else if (active.size === 2) {
        // Begin pinch.
        const [a, b] = [...active.values()];
        const rug = this.#rugs.get(id);
        if (!rug) return;
        this.#pinch = {
          distance: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY),
          scale: rug.scale,
        };
        this.#drag = null;
      }
    });

    el.addEventListener('pointermove', (e) => {
      if (!active.has(e.pointerId)) return;
      active.set(e.pointerId, e);

      if (this.#pinch && active.size === 2) {
        const [a, b] = [...active.values()];
        const d = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
        const rug = this.#rugs.get(id);
        if (!rug) return;
        rug.scale = clamp(this.#pinch.scale * (d / this.#pinch.distance), 0.2, 4);
        this.#applyTransform(id);
      } else if (this.#drag && this.#drag.id === id) {
        const rug = this.#rugs.get(id);
        if (!rug) return;
        rug.x = this.#drag.originX + (e.clientX - this.#drag.startX);
        rug.y = this.#drag.originY + (e.clientY - this.#drag.startY);
        this.#applyTransform(id);
      }
    });

    const stop = (e) => {
      active.delete(e.pointerId);
      el.releasePointerCapture?.(e.pointerId);
      el.classList.remove('is-dragging');
      if (active.size < 2) this.#pinch = null;
      if (active.size === 0) this.#drag = null;
    };
    el.addEventListener('pointerup', stop);
    el.addEventListener('pointercancel', stop);

    // Mouse wheel scales when hovering with the selected rug.
    el.addEventListener('wheel', (e) => {
      if (this.#selectedId !== id) return;
      e.preventDefault();
      const rug = this.#rugs.get(id);
      if (!rug) return;
      const factor = e.deltaY < 0 ? 1.05 : 0.95;
      rug.scale = clamp(rug.scale * factor, 0.2, 4);
      this.#applyTransform(id);
    }, { passive: false });

    el.addEventListener('keydown', (e) => {
      if (this.#selectedId !== id) return;
      const rug = this.#rugs.get(id);
      if (!rug) return;
      const step = e.shiftKey ? 16 : 4;
      let handled = true;
      switch (e.key) {
        case 'ArrowLeft':  rug.x -= step; break;
        case 'ArrowRight': rug.x += step; break;
        case 'ArrowUp':    rug.y -= step; break;
        case 'ArrowDown':  rug.y += step; break;
        case 'Delete':
        case 'Backspace':  this.removeRug(); return;
        default: handled = false;
      }
      if (handled) {
        e.preventDefault();
        this.#applyTransform(id);
      }
    });
  }

  /* ──────────────────────────────────────────────
     Rotation slider
     ────────────────────────────────────────────── */

  /** @param {InputEvent} event */
  onRotationInput = (event) => {
    if (!this.#selectedId) return;
    const target = /** @type {HTMLInputElement} */ (event.target);
    const deg = Number(target.value);
    const rug = this.#rugs.get(this.#selectedId);
    if (!rug) return;
    rug.rotation = deg;
    this.#applyTransform(this.#selectedId);
    this.#updateRotationLabel(deg);
  };

  #updateRotationLabel(deg) {
    if (this.refs.rotationValue) {
      this.refs.rotationValue.textContent = `${Math.round(deg)}°`;
    }
  }

  /* ──────────────────────────────────────────────
     Compare mode
     ────────────────────────────────────────────── */

  toggleCompare = () => {
    const canvas = this.refs.canvas;
    const on = canvas.dataset.compare !== 'true';
    canvas.dataset.compare = on ? 'true' : 'false';
    if (this.refs.compareBtn) {
      this.refs.compareBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
    }
  };

  /* ──────────────────────────────────────────────
     Download — composite to a hidden canvas, save as PNG
     ────────────────────────────────────────────── */

  downloadImage = async () => {
    const room = /** @type {HTMLImageElement} */ (this.refs.roomImage);
    if (!room?.complete || !room.naturalWidth) return;

    // Render to the room image's natural resolution for a crisp export.
    const rect = this.refs.canvas.getBoundingClientRect();
    const renderW = room.naturalWidth;
    const renderH = room.naturalHeight;
    // Scale factor between rendered canvas pixels and the export resolution.
    const sx = renderW / rect.width;
    const sy = renderH / rect.height;

    const out = document.createElement('canvas');
    out.width = renderW;
    out.height = renderH;
    const ctx = out.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(room, 0, 0, renderW, renderH);

    if (this.refs.canvas.dataset.compare !== 'true') {
      // Paint each rug. Each rug's DOM transform is:
      //   translate(calc(-50% + x), calc(-50% + y)) rotate(r) scale(s)
      // The rug element is positioned at top:50%; left:50% in the canvas.
      // Its un-transformed width is 45% of the canvas width (CSS), aspect 4/3.
      const rugRect = { w: rect.width * 0.45, h: (rect.width * 0.45) * (3 / 4) };
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;

      for (const rug of this.#rugs.values()) {
        const img = rug.el.querySelector('img');
        if (!(img instanceof HTMLImageElement) || !img.complete || !img.naturalWidth) continue;

        const cx = (centerX + rug.x) * sx;
        const cy = (centerY + rug.y) * sy;

        // Match the DOM's `object-fit: contain` so non-4:3 rugs aren't stretched.
        const boxW = rugRect.w * sx;
        const boxH = rugRect.h * sy;
        const imgRatio = img.naturalWidth / img.naturalHeight;
        const boxRatio = boxW / boxH;
        let drawW, drawH;
        if (imgRatio > boxRatio) {
          drawW = boxW;
          drawH = boxW / imgRatio;
        } else {
          drawH = boxH;
          drawW = boxH * imgRatio;
        }

        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate((rug.rotation * Math.PI) / 180);
        ctx.scale(rug.scale, rug.scale);
        ctx.drawImage(img, -drawW / 2, -drawH / 2, drawW, drawH);
        ctx.restore();
      }
    }

    let dataUrl;
    try {
      dataUrl = out.toDataURL('image/png');
    } catch (err) {
      console.error('[room-visualizer] Download blocked by CORS — make sure room photos are served with appropriate headers.', err);
      return;
    }
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `${this.dataset.productHandle || 'room-visualizer'}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  /* ──────────────────────────────────────────────
     Tips card + rail visibility
     ────────────────────────────────────────────── */

  toggleTips = () => {
    const card = this.refs.tipsCard;
    if (!card) return;
    const isHidden = card.hasAttribute('hidden');
    if (isHidden) card.removeAttribute('hidden');
    else card.setAttribute('hidden', '');
    if (this.refs.tipsBtn) {
      this.refs.tipsBtn.setAttribute('aria-pressed', isHidden ? 'true' : 'false');
    }
  };

  toggleRail = () => {
    const rail = this.refs.rail;
    if (!rail) return;
    if (rail.hasAttribute('hidden')) rail.removeAttribute('hidden');
    else rail.setAttribute('hidden', '');
  };

  /* ──────────────────────────────────────────────
     Rail search
     ────────────────────────────────────────────── */

  /** @param {InputEvent} event */
  filterRail = (event) => {
    const target = /** @type {HTMLInputElement} */ (event.target);
    const q = target.value.toLowerCase().trim();
    this.querySelectorAll('.rv-rail-card').forEach((card) => {
      const title = card.getAttribute('data-product-title') || '';
      const handle = card.getAttribute('data-product-handle') || '';
      const match = !q || title.includes(q) || handle.includes(q);
      if (match) card.removeAttribute('hidden');
      else card.setAttribute('hidden', '');
    });
  };

  /* ──────────────────────────────────────────────
     Switch rug (click a rail card)
     ────────────────────────────────────────────── */

  /** @param {MouseEvent} event */
  switchProduct = (event) => {
    const card = /** @type {HTMLElement} */ (event.target).closest('[data-rug-src]');
    if (!(card instanceof HTMLElement)) return;
    const newSrc = card.dataset.rugSrc;
    if (!newSrc) return;

    // Update master src so future-added rugs use the new product.
    this.dataset.rugSrc = newSrc;
    this.dataset.rugAlt = card.dataset.rugAlt || '';
    this.dataset.productHandle = card.dataset.productHandle || this.dataset.productHandle;

    // Swap the image on every rug currently in the canvas.
    for (const rug of this.#rugs.values()) {
      const img = rug.el.querySelector('img');
      if (img instanceof HTMLImageElement) {
        img.crossOrigin = 'anonymous';
        img.alt = this.dataset.rugAlt;
        img.src = newSrc;
      }
    }

    // Mark active card.
    for (const c of this.querySelectorAll('.rv-rail-card.is-active')) {
      c.classList.remove('is-active');
    }
    card.classList.add('is-active');

    // If we haven't dropped any rug yet (selector flow not run), drop one.
    if (this.#rugs.size === 0 && this.refs.roomImage instanceof HTMLImageElement && this.refs.roomImage.src) {
      this.#addRug({ x: 0, y: 0, rotation: 0, scale: 1 });
    }
  };

  /* ──────────────────────────────────────────────
     Wishlist (visual + persisted)
     ────────────────────────────────────────────── */

  #loadWishlist() {
    try {
      const raw = localStorage.getItem('rv_favorites');
      const arr = raw ? JSON.parse(raw) : [];
      this.#wishlist = new Set(Array.isArray(arr) ? arr : []);
    } catch {
      this.#wishlist = new Set();
    }
  }

  #saveWishlist() {
    try {
      localStorage.setItem('rv_favorites', JSON.stringify([...this.#wishlist]));
    } catch {
      /* localStorage disabled — silently no-op */
    }
  }

  #applyWishlistMarkers() {
    for (const btn of this.querySelectorAll('.rv-rail-card__heart')) {
      const handle = btn.getAttribute('data-product-handle') || '';
      btn.classList.toggle('is-wished', this.#wishlist.has(handle));
    }
    if (this.refs.rail) {
      this.refs.rail.dataset.hasWishlist = this.#wishlist.size > 0 ? 'true' : 'false';
    }
  }

  /** @param {MouseEvent} event */
  toggleWishlist = (event) => {
    event.stopPropagation();
    event.preventDefault();
    const btn = /** @type {HTMLElement} */ (event.target).closest('[data-product-handle]');
    if (!(btn instanceof HTMLElement)) return;
    const handle = btn.dataset.productHandle;
    if (!handle) return;
    if (this.#wishlist.has(handle)) this.#wishlist.delete(handle);
    else this.#wishlist.add(handle);
    this.#saveWishlist();
    this.#applyWishlistMarkers();
  };
}

/* ──────────────────────────────────────────────
   Helpers
   ────────────────────────────────────────────── */

function clamp(v, min, max) {
  return Math.min(Math.max(v, min), max);
}

if (!customElements.get('room-visualizer')) {
  customElements.define('room-visualizer', RoomVisualizerComponent);
}
